import { Routes, Route, useNavigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useAuthStore } from './store/authStore';
import { usePushStore } from './store/pushStore';
import { reconcileSentDrafts } from './services/sendReconcile';
import { reconcileInbox } from './services/inbox/receiveReconcile';
import { setUnauthorizedHandler } from './api/client';
import { Spinner } from './atoms';

// Layouts
import SwipeableLayout from './layouts/SwipeableLayout';
import PortraitFrame from './layouts/PortraitFrame';

// Components
import ProtectedRoute from './components/ProtectedRoute';

// Update components
import ArrivalSplash, { shouldArrive } from './organisms/ArrivalSplash';
import ForceUpdateModal from './organisms/ForceUpdateModal';
import UpdatingOverlay from './organisms/UpdatingOverlay';
import GlobalToast from './organisms/GlobalToast';
import PushNudge from './organisms/PushNudge';
import BugHunt from './organisms/BugHunt';

// Pages (lazy loaded)
const FriendsPage = lazy(() => import('./pages/FriendsPage'));
const ConversationPage = lazy(() => import('./pages/ConversationPage'));
const CaptionEditorPage = lazy(() => import('./pages/CaptionEditorPage'));
const RecipientPickerPage = lazy(() => import('./pages/RecipientPickerPage'));
// Auth pages - passkey-only (recovery codes are the fallback)
const LoginPage = lazy(() => import('./pages/PasskeyLoginPage'));
const RegisterPage = lazy(() => import('./pages/PasskeyRegisterPage'));
const VaultPage = lazy(() => import('./pages/VaultPage'));
const StoryViewerPage = lazy(() => import('./pages/StoryViewerPage'));
const GroupChatPage = lazy(() => import('./pages/GroupChatPage'));
const BiblePage = lazy(() => import('./pages/BiblePage'));
const BibleReaderPage = lazy(() => import('./pages/BibleReaderPage'));
const AnnotationsPage = lazy(() => import('./pages/AnnotationsPage'));
const DevotionsPage = lazy(() => import('./pages/DevotionsPage'));
const JournalPage = lazy(() => import('./pages/JournalPage'));
const JournalEntryPage = lazy(() => import('./pages/JournalEntryPage'));
const JournalEditorPage = lazy(() => import('./pages/JournalEditorPage'));
const ReadingPlansPage = lazy(() => import('./pages/ReadingPlansPage'));
const ReadingPlanDetailPage = lazy(() => import('./pages/ReadingPlanDetailPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const DevicesPage = lazy(() => import('./pages/DevicesPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const BibleAIPage = lazy(() => import('./pages/BibleAIPage'));
const SetupGuidePage = lazy(() => import('./pages/SetupGuidePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

function LoadingSpinner() {
  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <Spinner />
    </div>
  );
}

function App() {
  const { checkAuth, isAuthenticated } = useAuthStore();
  const { syncPush } = usePushStore();
  const navigate = useNavigate();
  // Cold launch only — a reload (refresh, or the update reload) skips it (BUG-019)
  const [arriving, setArriving] = useState(shouldArrive);

  // Any 401 from a normal request means the session is gone — say so once,
  // app-wide, instead of letting screens fail behind a signed-in-looking UI
  // (BUG-069). Registered before the first request goes out.
  useEffect(() => {
    setUnauthorizedHandler(() => useAuthStore.getState().noteSessionExpired());
    return () => setUnauthorizedHandler(null);
  }, []);

  // Confirm the cookie session on app load (cached user = probably valid)
  useEffect(() => {
    if (isAuthenticated) {
      checkAuth();
    }
  }, []);

  // Re-register push on every boot: a subscription can vanish under us
  // (see pushStore header)
  useEffect(() => {
    if (isAuthenticated) {
      void syncPush();
    }
  }, [isAuthenticated, syncPush]);

  // BUG-065/075: drafts that were mid-send when the app died — or when a
  // delivery confirm was interrupted — ask the server whether they actually
  // landed and clear themselves if they did. At launch AND on every return to
  // the foreground: a phone that stays open for days never gets a second cold
  // start, and a draft left behind mid-session used to wait for one forever.
  // Throttled inside the service.
  useEffect(() => {
    if (!isAuthenticated) return;
    const reconcile = () => {
      const userId = useAuthStore.getState().user?.id;
      if (userId) {
        void reconcileSentDrafts(userId);
        // The receive side's mirror pass (FEAT-040 w3): downloads what
        // arrived while we were away, sheds what was viewed elsewhere.
        void reconcileInbox(userId);
      }
    };
    reconcile();
    const onVisible = () => {
      if (document.visibilityState === 'visible') reconcile();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAuthenticated]);

  // Notification taps deep-link into the app (SW posts PUSH_NAVIGATE)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_NAVIGATE' && typeof event.data.url === 'string') {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  return (
    <PortraitFrame>
      {/* Cold-launch arrival: the aperture mark draws + fires the shutter */}
      {arriving && <ArrivalSplash onDone={() => setArriving(false)} />}

      {/* Update UI. No soft banner (Nation, 2026-08-07: "just remove it and
          nuke it") — an update installs quietly and takes over on the next
          launch; Settings → About is the readout and the manual lever. The
          force modal stays: it is the only brake on a client too old to run. */}
      <ForceUpdateModal />
      <UpdatingOverlay />

      {/* Global toast notifications for background operations — including the
          app-wide Offline pill (FEAT-037), which replaced OfflineBanner */}
      <GlobalToast />

      {/* One-time "turn on notifications" nudge (signed-in users only) */}
      {isAuthenticated && <PushNudge />}

      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public routes - Passkey auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          {/* Setup guide - public */}
          <Route path="/setup" element={<SetupGuidePage />} />

        {/* Main swipeable layout (Chat | Camera | Faith) */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <SwipeableLayout />
            </ProtectedRoute>
          }
        />

        {/* Friends page (pushed from Chat section) */}
        <Route
          path="/friends"
          element={
            <ProtectedRoute>
              <FriendsPage />
            </ProtectedRoute>
          }
        />

        {/* Chat/conversation route (full-screen, no nav bar) */}
        <Route
          path="/chat/:friendId"
          element={
            <ProtectedRoute>
              <ConversationPage />
            </ProtectedRoute>
          }
        />

        {/* Group chat route (full-screen, no nav bar) */}
        <Route
          path="/groups/:groupId"
          element={
            <ProtectedRoute>
              <GroupChatPage />
            </ProtectedRoute>
          }
        />

        {/* Vault route (full-screen, no nav bar) */}
        <Route
          path="/vault"
          element={
            <ProtectedRoute>
              <VaultPage />
            </ProtectedRoute>
          }
        />

        {/* Story viewer route (full-screen, no nav bar) */}
        <Route
          path="/stories/:userId"
          element={
            <ProtectedRoute>
              <StoryViewerPage />
            </ProtectedRoute>
          }
        />

        {/* Bible page (pushed from Faith section) */}
        <Route
          path="/bible"
          element={
            <ProtectedRoute>
              <BiblePage />
            </ProtectedRoute>
          }
        />

        {/* Bible reader route (full-screen, no nav bar) */}
        <Route
          path="/bible/:book/:chapter"
          element={
            <ProtectedRoute>
              <BibleReaderPage />
            </ProtectedRoute>
          }
        />

        {/* Bible annotations route (full-screen, no nav bar) */}
        <Route
          path="/bible/annotations"
          element={
            <ProtectedRoute>
              <AnnotationsPage />
            </ProtectedRoute>
          }
        />

        {/* Devotions route */}
        <Route
          path="/devotions"
          element={
            <ProtectedRoute>
              <DevotionsPage />
            </ProtectedRoute>
          }
        />

        {/* Journal routes (full-screen, no nav bar) */}
        <Route
          path="/journal"
          element={
            <ProtectedRoute>
              <JournalPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journal/new"
          element={
            <ProtectedRoute>
              <JournalEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journal/:id"
          element={
            <ProtectedRoute>
              <JournalEntryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/journal/:id/edit"
          element={
            <ProtectedRoute>
              <JournalEditorPage />
            </ProtectedRoute>
          }
        />

        {/* Reading Plans routes (full-screen, no nav bar) */}
        <Route
          path="/reading-plans"
          element={
            <ProtectedRoute>
              <ReadingPlansPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reading-plans/:planId"
          element={
            <ProtectedRoute>
              <ReadingPlanDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reading-plans/subscription/:subscriptionId"
          element={
            <ProtectedRoute>
              <ReadingPlanDetailPage />
            </ProtectedRoute>
          }
        />

        {/* Settings route (full-screen, no nav bar) */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Where you're signed in (FEAT-013) */}
        <Route
          path="/settings/devices"
          element={
            <ProtectedRoute>
              <DevicesPage />
            </ProtectedRoute>
          }
        />

        {/* Admin route (admin only) */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          }
        />

        {/* Profile route (full-screen, no nav bar) */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Ask routes (full-screen, no nav bar) */}
        <Route
          path="/ask"
          element={
            <ProtectedRoute>
              <BibleAIPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ask/:conversationId"
          element={
            <ProtectedRoute>
              <BibleAIPage />
            </ProtectedRoute>
          }
        />

        {/* Camera preview/send routes (pushed from Camera section) */}
        <Route
          path="/camera/preview"
          element={
            <ProtectedRoute>
              <CaptionEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/camera/send"
          element={
            <ProtectedRoute>
              <RecipientPickerPage />
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>

    {/* Bug hunt reporter. Must stay mounted AFTER Routes: its Sheet is z-50,
        same as MediaViewer, and equal z-index resolves by DOM order, so this
        position is what lets the report sheet paint over fullscreen viewers. */}
    {isAuthenticated && <BugHunt />}
    </PortraitFrame>
  );
}

export default App;
